import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Empty, ErrorBlock, Price, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities, useMe } from "../../api/queries.ts";
import {
  STATUS_LABEL,
  TXN_LABEL,
  TYPE_LABEL,
  type EarningsResp,
  type FeeDepositRow,
  type FeeSubRow,
  type TxnRow,
} from "../earnings.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 收益明细
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **对账页在网页上必须是表。**
 *    手机版每一笔占三行（类型 / 备注 / 时间 + 右边金额），十笔就要滚半天；
 *    而对账的动作是"**从上往下顺着看余额对不对得上**" ——
 *    这件事在一列对齐的表格里三十秒能做完，在卡片流里要做一下午。
 *
 * ⚠️ 资金流水那张表把「变动后余额」排在最后一列且右对齐 ——
 *    对账就是顺着这一列往下读。
 */

const orgGet = <T,>(path: string, orgId: number) => orgFetch<T>(path, orgId);

export function DesktopEarnings() {
  const search = useSearch({ from: "/earnings" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  const orgs = (identities?.orgs ?? []).filter((o) => o.status === "active");
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;

  const [tab, setTab] = useState<"items" | "txns" | "fees">("items");

  const data = useQuery({
    queryKey: ["earnings-detail", orgId],
    queryFn: () => orgGet<EarningsResp>("/earnings?limit=100", orgId!),
    enabled: Boolean(orgId) && tab === "items",
  });

  const txns = useQuery({
    queryKey: ["txns", orgId],
    queryFn: () => orgGet<{ items: TxnRow[] }>("/txns?limit=100", orgId!),
    enabled: Boolean(orgId) && tab === "txns",
  });

  /**
   * 🔴 **押金要单独摆出来。** 它交的时候就从余额扣走了，
   *    既不在可提现余额里、也不在待结算里 —— 不列出来的话
   *    商家只会看到"钱少了一笔"，来问客服客服也答不上来。
   */
  const fees = useQuery({
    queryKey: ["fee-account", orgId],
    queryFn: () =>
      orgGet<{ subscriptions: FeeSubRow[]; deposits: FeeDepositRow[]; held: number }>(
        "/fee-account",
        orgId!,
      ),
    enabled: Boolean(orgId) && tab === "fees",
  });

  if (mePending)
    return (
      <AccountShell title="收益明细">
        <Spinner compact />
      </AccountShell>
    );
  if (!me || orgs.length === 0)
    return (
      <AccountShell title="收益明细">
        <Empty title="你还不是任何商家的成员" compact />
      </AccountShell>
    );

  return (
    <AccountShell title="收益明细">
      {orgs.length > 1 && (
        <div className="mb-4 flex gap-2">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={cn(
                "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
                o.id === orgId
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-5 flex border-b border-border">
        {(
          [
            ["items", "分账明细"],
            ["txns", "资金流水"],
            ["fees", "费用与押金"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={cn(
              "relative px-4 py-2.5 text-[15px] transition-colors",
              tab === k ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
            {tab === k && (
              <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand-600" />
            )}
          </button>
        ))}
      </div>

      {/* ══════════ 分账明细 ══════════ */}
      {tab === "items" &&
        (data.isPending ? (
          <Spinner compact />
        ) : data.error ? (
          <ErrorBlock error={data.error} onRetry={() => data.refetch()} />
        ) : (
          <>
            <div className="mb-4 grid grid-cols-4 gap-4">
              {[
                ["待结算", data.data.summary.pending],
                ["可结算", data.data.summary.settleable],
                ["已入账", data.data.summary.settled],
                ["已冲抵", data.data.summary.reversed],
              ].map(([label, v]) => (
                <div
                  key={label as string}
                  className="rounded-xl border border-border bg-card px-5 py-4"
                >
                  <p className="text-[13px] text-muted-foreground">{label}</p>
                  <p className="mt-1.5">
                    <Price cents={v as number} className="text-[22px]" />
                  </p>
                </div>
              ))}
            </div>

            {/* ⚠️ 说清楚为什么有负数，不解释的话商家会以为系统扣错了。 */}
            <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
              负数是<strong className="text-foreground">你支出的部分</strong>
              ：自己发的优惠券、被上级抽的成、退款时扣回的钱。
            </p>

            {data.data.items.length === 0 ? (
              <Empty title="还没有收益记录" hint="有客户下单后，每一笔分账都会列在这里" compact />
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="grid grid-cols-[150px_110px_minmax(0,1fr)_150px_140px] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
                  <span>类型</span>
                  <span>状态</span>
                  <span>说明</span>
                  <span>时间</span>
                  <span className="text-right">金额</span>
                </div>
                {data.data.items.map((it) => (
                  <div
                    key={it.id}
                    className="grid grid-cols-[150px_110px_minmax(0,1fr)_150px_140px] items-center gap-4 border-t border-border px-5 py-3 text-[14px]"
                  >
                    <span>{TYPE_LABEL[it.type] ?? it.type}</span>
                    <span>
                      <span className="rounded bg-secondary px-1.5 py-0.5 text-[12px] text-muted-foreground">
                        {STATUS_LABEL[it.status] ?? it.status}
                      </span>
                    </span>
                    <span className="min-w-0 truncate text-[13px] text-muted-foreground">
                      订单 #{it.orderId}
                      {it.note ? ` · ${it.note}` : ""}
                    </span>
                    <span className="text-[13px] text-muted-foreground">
                      {new Date(it.createdAt).toLocaleDateString("zh-CN")}
                      {/* 待结算的钱要等售后期，标出解冻时间省得他反复问。 */}
                      {it.status === "pending" && it.settleableAt && (
                        <span className="mt-0.5 block text-[12px]">
                          {new Date(it.settleableAt).toLocaleDateString("zh-CN")} 可结
                        </span>
                      )}
                    </span>
                    <span
                      className={cn(
                        "text-right",
                        it.amount < 0 ? "text-red-600" : "text-brand-600",
                      )}
                    >
                      {it.amount < 0 ? "−" : "+"}
                      <Price cents={Math.abs(it.amount)} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        ))}

      {/* ══════════ 资金流水 ══════════ */}
      {tab === "txns" &&
        (txns.isPending ? (
          <Spinner compact />
        ) : txns.error ? (
          <ErrorBlock error={txns.error} onRetry={() => txns.refetch()} />
        ) : (txns.data?.items.length ?? 0) === 0 ? (
          <Empty title="还没有资金流水" hint="结算入账、提现都会记在这里" compact />
        ) : (
          <>
            {/*
              🔴 这一句是对账的关键：每笔流水都带**变动后的余额**，
                 所以"钱去哪了"永远查得到，不用靠猜。
            */}
            <p className="mb-4 text-[13px] text-muted-foreground">
              每一笔都记着<strong className="text-foreground">变动后的余额</strong>
              ，顺着最后一列从上往下就能对上账。
            </p>
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-[160px_minmax(0,1fr)_180px_140px_150px] gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
                <span>类型</span>
                <span>说明</span>
                <span>时间</span>
                <span className="text-right">变动</span>
                <span className="text-right">变动后余额</span>
              </div>
              {txns.data!.items.map((t) => (
                <div
                  key={t.id}
                  className="grid grid-cols-[160px_minmax(0,1fr)_180px_140px_150px] items-center gap-4 border-t border-border px-5 py-3 text-[14px]"
                >
                  <span>{TXN_LABEL[t.type] ?? t.type}</span>
                  <span className="min-w-0 truncate text-[13px] text-muted-foreground">
                    {t.note}
                  </span>
                  <span className="text-[13px] text-muted-foreground tabular-nums">
                    {new Date(t.createdAt).toLocaleString("zh-CN")}
                  </span>
                  <span
                    className={cn("text-right", t.amount < 0 ? "text-red-600" : "text-brand-600")}
                  >
                    {t.amount < 0 ? "−" : "+"}
                    <Price cents={Math.abs(t.amount)} />
                  </span>
                  <span className="text-right tabular-nums">
                    <Price cents={t.balanceAfter} />
                  </span>
                </div>
              ))}
            </div>
          </>
        ))}

      {/* ══════════ 费用与押金 ══════════ */}
      {tab === "fees" &&
        (fees.isPending ? (
          <Spinner compact />
        ) : fees.error ? (
          <ErrorBlock error={fees.error} onRetry={() => fees.refetch()} />
        ) : (
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="text-[13px] text-muted-foreground">押在平台的保证金</p>
              <p className="mt-1.5">
                <Price cents={fees.data!.held} className="text-[26px]" />
              </p>
              {/* 🔴 说清楚这笔钱去哪了，否则商家以为系统把钱吞了。 */}
              <p className="mt-2 max-w-[52ch] text-[13px] leading-relaxed text-muted-foreground">
                这笔钱交的时候就从余额扣走了，<strong className="text-foreground">
                  不在可提现余额里
                </strong>
                ；退出渠道时会退回你的余额。
              </p>
            </div>

            <div>
              <p className="mb-2.5 text-[15px] font-medium">周期费</p>
              {fees.data!.subscriptions.length === 0 ? (
                <p className="rounded-xl border border-border bg-card px-5 py-4 text-[14px] text-muted-foreground">
                  平台没有向你收年费或月费。
                </p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                  {fees.data!.subscriptions.map((s) => (
                    <div key={s.id} className="border-b border-border px-5 py-3.5 last:border-b-0">
                      <p className="text-[14px]">
                        {s.period === "monthly"
                          ? "月费"
                          : s.period === "quarterly"
                            ? "季费"
                            : "年费"}
                        <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[12px] text-muted-foreground">
                          {s.status === "active"
                            ? "生效中"
                            : s.status === "expired"
                              ? "已过期"
                              : "已取消"}
                        </span>
                        <span className="ml-3 text-muted-foreground">
                          每期 <Price cents={s.amount} />
                        </span>
                        {s.nextBillAt && (
                          <span className="ml-3 text-[13px] text-muted-foreground">
                            {new Date(s.nextBillAt).toLocaleDateString("zh-CN")} 扣下一期
                          </span>
                        )}
                      </p>
                      {/* ⚠️ 过期不等于停售 —— 这句话必须说，否则商家会慌。 */}
                      {s.status === "expired" && (
                        <p className="mt-1 text-[13px] text-amber-700">
                          上次扣款时余额不足，已暂停续期。
                          <strong>不影响你正常卖货</strong>。
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="mb-2.5 text-[15px] font-medium">保证金记录</p>
              {fees.data!.deposits.length === 0 ? (
                <p className="rounded-xl border border-border bg-card px-5 py-4 text-[14px] text-muted-foreground">
                  没有保证金记录。
                </p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                  {fees.data!.deposits.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center gap-4 border-b border-border px-5 py-3.5 last:border-b-0"
                    >
                      <span className="w-24 text-[14px]">
                        {d.status === "held"
                          ? "在押"
                          : d.status === "refunded"
                            ? "已退还"
                            : d.status === "forfeited"
                              ? "已没收"
                              : d.status}
                      </span>
                      <span className="min-w-0 flex-1 text-[13px] text-red-600">
                        {d.forfeitReason}
                      </span>
                      <span className="text-[13px] text-muted-foreground">
                        {d.paidAt ? new Date(d.paidAt).toLocaleDateString("zh-CN") : ""}
                      </span>
                      <Price cents={d.amount} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
    </AccountShell>
  );
}
